/**
 * ============================================================
 * KEJJA OS — src/pages/pantry/PantryExpiringPage.tsx
 * ============================================================
 */

import { Clock, CheckCircle2 } from 'lucide-react';
import { usePantryContext } from './PantryLayout';
import { useExpiringSoon } from '@/hooks/usePantry';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import PantryRow from './PantryRow';

export default function PantryExpiringPage() {
  const { homeId } = usePantryContext();
  const { data, isLoading } = useExpiringSoon(homeId, 14);
  const items = data?.items ?? [];

  if (isLoading) return <Skeleton className="h-48" />;

  if (items.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500/60 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Nothing expiring in the next 2 weeks.</p>
        </CardContent>
      </Card>
    );
  }

  const expired = items.filter((i) => (i.daysUntilExpiry ?? 1) < 0);
  const thisWeek = items.filter((i) => (i.daysUntilExpiry ?? 99) >= 0 && (i.daysUntilExpiry ?? 99) <= 7);
  const nextWeek = items.filter((i) => (i.daysUntilExpiry ?? 0) > 7);

  return (
    <div className="space-y-4 pb-16">
      {expired.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-rose-600 mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Expired ({expired.length})
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {expired.map((it) => <PantryRow key={it.id} item={it} homeId={homeId} />)}
            </CardContent>
          </Card>
        </section>
      )}
      {thisWeek.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-orange-600 mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Within 7 days ({thisWeek.length})
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {thisWeek.map((it) => <PantryRow key={it.id} item={it} homeId={homeId} />)}
            </CardContent>
          </Card>
        </section>
      )}
      {nextWeek.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Week after ({nextWeek.length})
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {nextWeek.map((it) => <PantryRow key={it.id} item={it} homeId={homeId} />)}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
